/**
 * Parses a page number from a string parameter with validation.
 * Returns 1 as default for invalid or missing values.
 * @param pageParam - The page parameter string to parse
 * @returns A valid page number (integer > 0) or 1 as default
 */
export function parsePageNumber(pageParam: string | null): number {
  if (!pageParam) {
    return 1
  }
  const parsedPage = Number(pageParam)
  const isValidPageParam = Number.isInteger(parsedPage) && parsedPage > 0
  return isValidPageParam ? parsedPage : 1
}

/**
 * Encodes a path part, preserving forward slashes (e.g. branch names like "feature/foo").
 */
function encodePart(part: string): string {
  return part.split('/').map(encodeURIComponent).join('/')
}

/**
 * Generates the encoded URL path to an automation's .md file in the repository blob view.
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param defaultBranch - Default branch of the repository
 * @param automationName - Name of the automation (without .md extension)
 * @returns Encoded URL path (e.g., "/owner/repo/blob/main/.github/agents/my%20automation.md")
 */
export function automationFilePath(owner: string, repo: string, defaultBranch: string, automationName: string): string {
  return `/${owner}/${repo}/blob/${encodePart(defaultBranch)}/.github/agents/${encodeURIComponent(automationName)}.md`
}

const COPILOT_APP_BASE_URL = 'https://github.com/copilot/app/launch'

/**
 * Builds a GitHub Copilot App deep link URL for an automation.
 * @param automationId - The automation ID
 * @returns Launch URL that opens the automation in the Copilot App
 */
export function copilotAppWorkflowUrl(automationId: string): string {
  return `${COPILOT_APP_BASE_URL}?open=${encodeURIComponent(`ghapp://workflows/${automationId}?mode=cloud`)}`
}

/**
 * Builds a GitHub Copilot App deep link URL for an automation task.
 * @param automationId - The automation ID
 * @param taskId - The task ID
 * @returns Launch URL that opens the task in the Copilot App
 */
export function copilotAppTaskUrl(automationId: string, taskId: string): string {
  return `${COPILOT_APP_BASE_URL}?open=${encodeURIComponent(`ghapp://workflows/${automationId}/${taskId}?mode=cloud`)}`
}
